/* eslint-disable react/jsx-props-no-spreading */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
	debounce, get, isUndefined, cloneDeep,
} from 'lodash';

/**
 * Adds a debounce time to a Component's onChange function
 *
 * @param WrappedComponent The component that will be wrapped
 * @param valueSrc The path to the value property in the event object, set to null if the value is the event itself
 * @param debounceTime The amount of time the onChange function will be debounced
 * @returns Component
 */
function withDebounce(WrappedComponent, valueSrc = 'target.value', debounceTime = 300) {
	class WithDebounce extends Component {
		constructor(props) {
			super(props);
			this.state = {
				value : props.value,
			};

			// The inherited onChange function gets debounced to run it only once after the user has stopped
			// interacting with the component for an amount of time
			this.handleInheritOnChange = debounce(this.handleInheritOnChange, debounceTime);
		}

		shouldComponentUpdate(nextProps, nextState) {
			const lastState = this.state;
			const lastProps = this.props;
			return (
				nextState.value !== lastState.value
				|| nextProps.disabled !== lastProps.disabled
				// The component should also update when the received value through props change
				|| nextProps.value !== lastState.value
			);
		}

		componentDidUpdate(prevProps) {
			const nextProps = this.props;

			// Update value through prop only when the received value is different from the current one
			if (prevProps.value !== nextProps.value) this.updateValueWithProp();
		}

		componentWillUnmount() {
			this.handleInheritOnChange.cancel();
		}

		updateValueWithProp = () => {
			const newState = cloneDeep(this.state);
			const { value } = this.props;
			this.setState({ ...newState, value });
		};

		handleChange = (event) => {
			if (!isUndefined(event.persist)) event.persist();
			const newState = { ...this.state, value : valueSrc !== null ? get(event, valueSrc, '') : event };
			this.setState(newState, () => {
				this.handleInheritOnChange(event);
			});
		};

		handleInheritOnChange = (event) => {
			const { onChange } = this.props;

			// The inherited onChange function will run with its event unmodified
			onChange(event);
		};

		render() {
			const { value } = this.state;

			return (
				<WrappedComponent
					{...this.props}
					onChange={this.handleChange}
					value={value}
				/>
			);
		}
	}

	WithDebounce.propTypes = {
		disabled : PropTypes.bool,
		onChange : PropTypes.func.isRequired,
		value    : PropTypes.any,
	};

	WithDebounce.defaultProps = {
		disabled : false,
		value    : '',
	};

	WithDebounce.displayName = `WithDebounce(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

	WithDebounce.propTypes = {
		value    : PropTypes.any,
		disabled : PropTypes.bool,
		onChange : PropTypes.func,
	};

	WithDebounce.defaultProps = {
		value    : '',
		disabled : false,
		onChange : () => { },
	};

	return WithDebounce;
}

export default withDebounce;
